/**
 * Rekonsiliasi A.R. — Fase 1 (2026-10-13, user request).
 *
 * READ-ONLY. Satu sumber: GET /api/ar/outstanding-report (utils/ar_engine).
 * 5+1 metrik terpisah: Piutang Belum Diselesaikan · Piutang Jatuh Tempo ·
 * Retensi Belum Diklaim (3 status due) · Retensi Diklaim-Belum Dibayar ·
 * Penerimaan Belum Teralokasi · Selisih Perlu Rekonsiliasi.
 * Tooltip formula per kartu · tanggal acuan · scope permission ·
 * tab "Per Induk" & "Per Invoice".
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Info, RefreshCw, ShieldAlert, ShieldCheck, Wallet, AlertTriangle, Inbox, Landmark } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatDetail } from "@/lib/api";
import { formatIDR } from "@/lib/constants";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import ReceiptClassificationQueue from "@/components/ar/ReceiptClassificationQueue";
import BankUnallocatedQueue from "@/components/ar/BankUnallocatedQueue";

const RECON_STATUS_META = {
  belum_ditinjau: { label: "Belum Ditinjau", cls: "text-slate-600 bg-slate-100 border-slate-300" },
  belum_lengkap: { label: "Belum Lengkap", cls: "text-amber-800 bg-amber-100 border-amber-300" },
  lengkap: { label: "Lengkap", cls: "text-emerald-800 bg-emerald-100 border-emerald-300" },
  perlu_ditinjau_ulang: { label: "Perlu Ditinjau Ulang", cls: "text-rose-800 bg-rose-100 border-rose-300" },
};

const RET_STATUS_LABEL = {
  belum_jatuh_tempo: "Belum jatuh tempo",
  jatuh_tempo_belum_diklaim: "Sudah jatuh tempo, belum diklaim",
  jatuh_tempo_belum_diketahui: "Jatuh tempo belum diketahui",
};

function MetricCard({ defKey, value, defs, tone, testid, sub }) {
  const d = defs?.[defKey] || {};
  return (
    <div className={`border bg-white p-3 ${tone === "rose" ? "border-rose-200" : tone === "amber" ? "border-amber-200" : "border-slate-200"}`} data-testid={testid} title={`${d.label}\nRumus: ${d.formula}\nSumber: ${d.source}`}>
      <div className="flex items-center gap-1">
        <span className="text-[9px] uppercase tracking-wider text-slate-500 truncate">{d.label || defKey}</span>
        <Info size={10} className="ml-auto text-slate-300 shrink-0" />
      </div>
      <div className="font-display text-lg tabular-nums text-[#0B1D3A] mt-1">{formatIDR(value ?? 0)}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function RekonsiliasiArPage() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { user } = useAuth();
  const { canPerm } = useAccess();
  const canConfirm = canPerm(user, "collection", "update") || canPerm(user, "finance_report", "update");
  const [asOf, setAsOf] = useState("");
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [status, setStatus] = useState(null);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  // 2026-10-22 (user request): tab baru "Antrean Penerimaan" + "Mutasi Bank"
  // untuk klasifikasi FAT. Default masih "head" (ringkasan metrik).
  const initialTab = sp.get("tab") || "head";
  const [tab, setTab] = useState(initialTab);
  const initialFilter = sp.get("filter") || "";
  const initialProject = sp.get("project") || "";
  const onTabChange = (k) => {
    setTab(k);
    const next = new URLSearchParams(sp);
    if (k === "head") next.delete("tab"); else next.set("tab", k);
    setSp(next, { replace: true });
  };

  const load = useCallback(() => {
    setLoading(true); setErr(null);
    api.get(`/ar/outstanding-report${asOf ? `?as_of=${asOf}` : ""}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        setErr(formatDetail(e.response?.data?.detail) || "Gagal memuat rekonsiliasi.");
        setData(null);
        if (e.response?.status === 403) toast.error("Anda tidak memiliki akses halaman ini.");
      })
      .finally(() => setLoading(false));
  }, [asOf]);
  const loadStatus = useCallback(() => {
    api.get(`/ar/recon-status?period=${period}`).then((r) => setStatus(r.data)).catch(() => setStatus(null));
  }, [period]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const confirmStatus = async (next) => {
    const label = next === "lengkap" ? "Lengkap" : "Belum Lengkap";
    const note = window.prompt(`Konfirmasi status "${label}" untuk periode ${period}.\nCatatan (periode, bukti/dasar pemeriksaan):`, "");
    if (note === null) return;
    try {
      await api.post("/ar/recon-status", { period, status: next, note });
      toast.success(`Status periode ${period}: ${label}. Tercatat di audit.`);
      loadStatus();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  const m = data?.metrics;
  const defs = data?.definitions;

  return (
    <div className="p-6 flex flex-col gap-4" data-testid="ar-recon-page">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={() => nav(-1)} className="p-1.5 border border-slate-200 hover:bg-slate-50" data-testid="ar-recon-back"><ArrowLeft size={14} /></button>
        <div className="flex-1">
          <h1 className="font-display text-xl tracking-tight text-[#0B1D3A] flex items-center gap-2"><Wallet size={18} /> Rekonsiliasi A.R.</h1>
          <p className="text-[11px] text-slate-500">Read-only · satu engine perhitungan bersama · tidak mengubah transaksi</p>
        </div>
        <label className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
          Tanggal acuan
          <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} data-testid="ar-recon-asof"
            className="h-7 text-xs border border-slate-300 px-2" />
        </label>
        <button onClick={load} disabled={loading} data-testid="ar-recon-reload"
          className="h-7 px-2.5 border border-slate-300 text-[10px] uppercase tracking-wider font-semibold hover:bg-slate-50 flex items-center gap-1">
          <RefreshCw size={10} className={loading ? "animate-spin" : ""} /> Muat Ulang
        </button>
      </div>

      {err && <div className="border border-rose-200 bg-rose-50 px-3 py-2 text-[11px] text-rose-700" data-testid="ar-recon-err">{err}</div>}
      {loading && !data && <div className="p-6 text-xs text-slate-400 italic text-center">Memuat rekonsiliasi…</div>}

      {data && (
        <>
          {/* Status kelengkapan rekonsiliasi per periode (Fase 3) */}
          {status && (
            <div className="border border-slate-200 bg-white p-3 flex items-center gap-3 flex-wrap" data-testid="ar-recon-status">
              <span className="text-[10px] uppercase tracking-wider text-slate-500">Kelengkapan periode</span>
              <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="ar-recon-period"
                className="h-7 text-xs border border-slate-300 px-2" />
              <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 border ${RECON_STATUS_META[status.status]?.cls}`} data-testid="ar-recon-status-badge">
                {RECON_STATUS_META[status.status]?.label || status.status}
              </span>
              {status.confirmation && (
                <span className="text-[10px] text-slate-500" data-testid="ar-recon-status-meta">
                  oleh {status.confirmation.confirmed_by_name} · {new Date(status.confirmation.confirmed_at).toLocaleString("id-ID")}
                  {status.confirmation.note ? ` · "${status.confirmation.note}"` : ""}
                </span>
              )}
              {status.status === "perlu_ditinjau_ulang" && (
                <span className="text-[10px] text-rose-700 italic" data-testid="ar-recon-status-changed">
                  Transaksi relevan berubah setelah konfirmasi terakhir — konfirmasi ulang diperlukan (histori tetap tersimpan).
                </span>
              )}
              {canConfirm && (
                <span className="ml-auto flex items-center gap-1.5">
                  <button type="button" onClick={() => confirmStatus("belum_lengkap")} data-testid="ar-recon-mark-incomplete"
                    className="h-7 px-2.5 border border-amber-300 text-amber-800 text-[10px] uppercase tracking-wider font-semibold hover:bg-amber-50 flex items-center gap-1">
                    <ShieldAlert size={10} /> Tandai Belum Lengkap
                  </button>
                  <button type="button" onClick={() => confirmStatus("lengkap")} data-testid="ar-recon-confirm-complete"
                    className="h-7 px-2.5 border border-emerald-300 text-emerald-800 text-[10px] uppercase tracking-wider font-semibold hover:bg-emerald-50 flex items-center gap-1">
                    <ShieldCheck size={10} /> Konfirmasi Lengkap
                  </button>
                </span>
              )}
            </div>
          )}

          <div className="border border-sky-200 bg-sky-50 px-3 py-2 text-[11px] text-sky-900 flex items-start gap-2" data-testid="ar-recon-note">
            <Info size={13} className="mt-0.5 shrink-0" />
            <span>{data.note} · Scope: <b>{data.scope}</b> · Per <b>{data.as_of}</b></span>
          </div>

          {/* 6 kartu metrik */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2" data-testid="ar-recon-metrics">
            <MetricCard defKey="ar_outstanding" value={m.ar_outstanding} defs={defs} testid="ar-m-outstanding" sub={`${(data.invoice_rows || []).filter((r) => r.remaining > 0).length} invoice belum selesai`} />
            <MetricCard defKey="ar_overdue" value={m.ar_overdue} defs={defs} tone="rose" testid="ar-m-overdue" sub={`${(data.invoice_rows || []).filter((r) => r.is_overdue).length} invoice lewat jatuh tempo`} />
            <MetricCard defKey="retention_not_claimed" value={m.retention_not_claimed_total} defs={defs} tone="amber" testid="ar-m-ret-notclaimed"
              sub={Object.entries(m.retention_not_claimed).filter(([, v]) => v > 0).map(([k, v]) => `${RET_STATUS_LABEL[k]}: ${formatIDR(v)}`).join(" · ") || "—"} />
            <MetricCard defKey="retention_claimed_unpaid" value={m.retention_claimed_unpaid} defs={defs} testid="ar-m-ret-claimed" sub="sisa tagihan klaim setelah pembayaran parsial" />
            <MetricCard defKey="receipts_unallocated" value={data.receipts_unallocated} defs={defs} tone="amber" testid="ar-m-unallocated" sub="mutasi masuk e-statement belum teralokasi" />
            <MetricCard defKey="selisih_perlu_rekonsiliasi" value={m.selisih_perlu_rekonsiliasi} defs={defs} tone="rose" testid="ar-m-selisih"
              sub={`kelebihan bayar ${formatIDR(m.overpaid_total)} + belum teralokasi + legacy ambigu ${formatIDR(m.ambiguous_legacy_total)}`} />
          </div>

          {m.cancelled_invoices?.count > 0 && (
            <div className="border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600" data-testid="ar-recon-cancelled">
              <ShieldAlert size={12} className="inline mr-1 text-slate-500" />
              {m.cancelled_invoices.count} invoice dibatalkan ({formatIDR(m.cancelled_invoices.total)}) — dikeluarkan dari perhitungan, tetap tercatat di riwayat.
            </div>
          )}

          {/* Tab Per Induk / Per Invoice / Antrean Penerimaan / Mutasi Bank */}
          <div className="flex items-center gap-1 border-b border-slate-200 flex-wrap">
            {[
              ["head", "Per Induk (gabungan VO/WO)", null],
              ["invoice", "Per Invoice", null],
              ["receipts", "Antrean Penerimaan", Inbox],
              ["bank", "Mutasi Bank Belum Teralokasi", Landmark],
            ].map(([k, l, Icon]) => (
              <button key={k} type="button" onClick={() => onTabChange(k)} data-testid={`ar-recon-tab-${k}`}
                className={`px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold border-b-2 flex items-center gap-1 ${tab === k ? "border-[#FF5A1F] text-[#0B1D3A]" : "border-transparent text-slate-500"}`}>
                {Icon && <Icon size={11} />}{l}
              </button>
            ))}
          </div>

          {tab === "receipts" && (
            <ReceiptClassificationQueue initialFilter={initialFilter} initialProject={initialProject} />
          )}

          {tab === "bank" && <BankUnallocatedQueue />}

          {tab === "head" && (
            <div className="border border-slate-200 bg-white overflow-x-auto" data-testid="ar-recon-head-table">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-[9px] uppercase tracking-wider text-slate-600"><tr>
                  <th className="text-left px-3 py-2">Proyek (Induk)</th><th className="text-left px-3 py-2">Client</th>
                  <th className="text-right px-3 py-2">Invoice</th><th className="text-right px-3 py-2">Belum Diselesaikan</th>
                  <th className="text-right px-3 py-2">Jatuh Tempo</th><th className="text-right px-3 py-2">Kelebihan Bayar</th>
                </tr></thead>
                <tbody>
                  {data.per_head.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400 italic">Tidak ada proyek dalam scope.</td></tr>}
                  {data.per_head.map((h) => (
                    <tr key={h.head_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`ar-head-${h.head_id}`}>
                      <td className="px-3 py-2 font-semibold">{h.project_name}</td>
                      <td className="px-3 py-2 text-[10px]">{h.client || "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{h.invoice_count}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold">{formatIDR(h.outstanding)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${h.overdue > 0 ? "text-rose-700 font-semibold" : ""}`}>{formatIDR(h.overdue)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-amber-700">{h.overpaid > 0 ? formatIDR(h.overpaid) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "invoice" && (
            <div className="border border-slate-200 bg-white overflow-x-auto max-h-[60vh] overflow-y-auto" data-testid="ar-recon-invoice-table">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-[9px] uppercase tracking-wider text-slate-600 sticky top-0"><tr>
                  <th className="text-left px-3 py-2">Invoice</th><th className="text-left px-3 py-2">Proyek</th><th className="text-left px-3 py-2">Client</th>
                  <th className="text-right px-3 py-2">Kas Harus Diterima</th><th className="text-right px-3 py-2">Teralokasi</th>
                  <th className="text-right px-3 py-2">Sisa</th><th className="text-left px-3 py-2">Jatuh Tempo</th><th className="text-left px-3 py-2">Catatan</th>
                </tr></thead>
                <tbody>
                  {data.invoice_rows.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-400 italic">Belum ada invoice dalam scope.</td></tr>}
                  {data.invoice_rows.map((r, i) => (
                    <tr key={r.entry_id || i} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`ar-inv-${r.entry_id || i}`}>
                      <td className="px-3 py-1.5 font-mono text-[10px]">{r.invoice_number || "—"}</td>
                      <td className="px-3 py-1.5">{r.project_name}</td>
                      <td className="px-3 py-1.5 text-[10px]">{r.client || "—"}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{formatIDR(r.cash_due)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{formatIDR(r.received)}</td>
                      <td className={`px-3 py-1.5 text-right tabular-nums font-semibold ${r.remaining > 0 ? (r.is_overdue ? "text-rose-700" : "text-[#0B1D3A]") : "text-emerald-700"}`}>
                        {r.remaining > 0 ? formatIDR(r.remaining) : r.overpaid > 0 ? `+${formatIDR(r.overpaid)}` : "Lunas"}
                      </td>
                      <td className="px-3 py-1.5 text-[10px] tabular-nums">{r.due_date || <span className="text-slate-400 italic">tidak diisi</span>}{r.is_overdue && <span className="ml-1 text-rose-600 font-semibold">LEWAT</span>}</td>
                      <td className="px-3 py-1.5 text-[9px] text-slate-500">
                        {r.ambiguous_legacy && <span className="text-amber-700">penerimaan legacy ambigu · </span>}
                        {r.has_allocated_receipts ? "pembayaran teralokasi" : "tanpa bukti cicilan"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Retensi & Selisih */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="border border-slate-200 bg-white p-3" data-testid="ar-recon-retention">
              <h3 className="font-display text-[13px] uppercase tracking-wider text-[#0B1D3A] mb-2">Retensi (terpisah dari piutang invoice)</h3>
              <table className="w-full text-xs">
                <tbody>
                  {Object.entries(m.retention_not_claimed).map(([k, v]) => (
                    <tr key={k} className="border-t border-slate-100">
                      <td className="py-1.5">{RET_STATUS_LABEL[k]}</td>
                      <td className="py-1.5 text-right tabular-nums font-semibold">{formatIDR(v)}</td>
                    </tr>
                  ))}
                  <tr className="border-t border-slate-100">
                    <td className="py-1.5">Sudah diklaim, belum dibayar</td>
                    <td className="py-1.5 text-right tabular-nums font-semibold">{formatIDR(m.retention_claimed_unpaid)}</td>
                  </tr>
                </tbody>
              </table>
              <p className="text-[10px] text-slate-500 italic mt-2">Tanggal jatuh tempo kosong TIDAK diasumsikan belum jatuh tempo — diberi status "belum diketahui" agar ditinjau FAT.</p>
            </div>
            <div className="border border-rose-200 bg-rose-50/40 p-3" data-testid="ar-recon-selisih">
              <h3 className="font-display text-[13px] uppercase tracking-wider text-rose-900 mb-2 flex items-center gap-1.5"><AlertTriangle size={13} /> Selisih Perlu Rekonsiliasi</h3>
              <table className="w-full text-xs">
                <tbody>
                  <tr className="border-t border-rose-100"><td className="py-1.5">Kelebihan bayar (per invoice)</td><td className="py-1.5 text-right tabular-nums font-semibold">{formatIDR(m.overpaid_total)}</td></tr>
                  <tr className="border-t border-rose-100"><td className="py-1.5">Penerimaan belum teralokasi (e-statement)</td><td className="py-1.5 text-right tabular-nums font-semibold">{formatIDR(data.receipts_unallocated)}</td></tr>
                  <tr className="border-t border-rose-100"><td className="py-1.5">Nilai entry legacy ambigu (tanpa bukti)</td><td className="py-1.5 text-right tabular-nums font-semibold">{formatIDR(m.ambiguous_legacy_total)}</td></tr>
                  <tr className="border-t-2 border-rose-300"><td className="py-1.5 font-bold">Total perlu ditinjau FAT</td><td className="py-1.5 text-right tabular-nums font-bold text-rose-800">{formatIDR(m.selisih_perlu_rekonsiliasi)}</td></tr>
                </tbody>
              </table>
              <p className="text-[10px] text-rose-800/80 italic mt-2">Angka ini bukan kerugian — komponen yang memerlukan peninjauan/alokasi berwenang. Tidak ada transaksi yang diubah dari halaman ini.</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
